import React, { ReactNode } from 'react';
import clsx from 'clsx';
import { Trans, useTranslation } from 'react-i18next';
import { ReactComponent as ClockIcon } from 'common-assets/assets/icons/clock-filled.svg';
import { ReactComponent as LockIcon } from 'common-assets/assets/icons/lock-filled.svg';
import { Card, Divider, Icon, Typography } from 'ui-components';
import {
  NO_VALUE_PLACEHOLDER,
  RETIREMENT_CALCULATOR_NAMESPACE,
} from '../../tools/RetirementCalculator.const';

import useStyles from './RetirementCalculatorPreview.style';

export const RetirementCalculatorPreview: React.FC<{ className?: string }> = ({ className }) => {
  const c = useStyles({});
  const [t] = useTranslation(RETIREMENT_CALCULATOR_NAMESPACE, { useSuspense: false });

  const steps: Array<{ key: string; content: ReactNode }> = [
    {
      key: 'forecast',
      content: (
        <Trans t={t} i18nKey="preview.steps.forecast" />
      ),
    },
    {
      key: 'savings',
      content: (
        <Trans
          t={t}
          i18nKey="preview.steps.savings"
          components={{ bold: <strong className={c.stepAccent} /> }}
        />
      ),
    },
    {
      key: 'actions',
      content: (
        <Trans
          t={t}
          i18nKey="preview.steps.actions"
          components={{ bold: <strong className={c.stepAccent} /> }}
        />
      ),
    },
  ];

  return (
    <div className={clsx(c.root, className)}>
      <Card className={clsx(c.card, c.resultCard)} testId="RetirementCalculatorPreview-result">
        <Typography variant="body1" component="p" fontWeight="bold">
          {t('preview.result.label')}
        </Typography>
        <Typography variant="h4" component="p" textColor="greys.30" className={c.resultValue}>
          {t('preview.result.empty-value', {
            placeholder: NO_VALUE_PLACEHOLDER,
          })}
        </Typography>
      </Card>

      <Card className={clsx(c.card, c.stepsCard)} testId="RetirementCalculatorPreview">
        <div className={c.content}>
          <p className={c.badge}>
            <Icon className={c.badgeIcon}>
              <ClockIcon />
            </Icon>
            <Typography variant="body2" component="span">
              {t('preview.duration-badge')}
            </Typography>
          </p>

          <Typography
            variant="h3"
            component="h2"
            fontWeight="extraBold"
            className={c.title}
            testId="RetirementCalculatorPreview-title"
          >
            {t('preview.title')}
          </Typography>

          <ol className={c.stepList}>
            {steps.map((step, index) => (
              <li key={step.key} className={c.step}>
                <span
                  className={clsx(c.stepNumber, index === 0 && c.stepNumberCurrent)}
                  aria-hidden="true"
                >
                  {index + 1}
                </span>
                <Typography variant="body1" component="span" className={c.stepText}>
                  {step.content}
                </Typography>
              </li>
            ))}
          </ol>

          <Divider className={c.divider} />

          <p className={c.privacyNote}>
            <Icon className={c.privacyIcon}>
              <LockIcon />
            </Icon>
            <Typography variant="body2" component="span" textColor="greys.40">
              {t('preview.privacy-note')}
            </Typography>
          </p>
        </div>
      </Card>
    </div>
  );
};
